/**
 * @fileoverview Workflow-embedded automations section (MOR-736)
 *
 * Rendered inside the workflow template editor (both the list and the
 * visual builder views) so automation rules are built as part of the
 * workflow itself. Rules created here are scoped to the workflow: they
 * only fire on jobs the workflow has been applied to, and they are
 * archived together with the workflow.
 */

import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Pencil, Plus, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import { Button, Switch } from "@/ui/primitives";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/ui/composed/overlays/alert-dialog";
import { extractErrorMessage } from "@/utils/errors";
import {
  AutomationRuleDialog,
  type EditableRule,
} from "./AutomationRuleDialog";
import { AutomationRuleSummary } from "./AutomationRuleSummary";

export function WorkflowAutomationsSection({
  orgId,
  templateId,
  templateName,
  taskTitles,
  canEdit,
}: {
  orgId: Id<"organizations">;
  templateId: Id<"taskTemplates">;
  templateName: string;
  /** Current draft task titles — offered as filter suggestions. */
  taskTitles: string[];
  canEdit: boolean;
}) {
  const { data: rules } = useQuery(
    convexQuery(api.automations.rules.listByTemplate, { templateId }),
  );
  const updateRule = useConvexMutation(api.automations.rules.update);
  const archiveRule = useConvexMutation(api.automations.rules.archive);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<EditableRule | undefined>();
  const [pendingDelete, setPendingDelete] = useState<{
    id: Id<"automationRules">;
    name: string;
  } | null>(null);

  const rows = rules ?? [];

  const openNew = () => {
    setEditing(undefined);
    setEditorOpen(true);
  };
  const openEdit = (rule: (typeof rows)[number]) => {
    setEditing({
      _id: rule._id,
      templateId: rule.templateId,
      templateName: rule.templateName,
      name: rule.name,
      description: rule.description,
      enabled: rule.enabled,
      trigger: rule.trigger,
      conditions: rule.conditions,
      actions: rule.actions,
    });
    setEditorOpen(true);
  };

  const handleToggle = async (
    ruleId: Id<"automationRules">,
    enabled: boolean,
  ) => {
    try {
      await updateRule({ ruleId, patch: { enabled } });
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not update the automation."));
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await archiveRule({ ruleId: pendingDelete.id });
      toast.success("Automation deleted.");
      setPendingDelete(null);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not delete the automation."));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-primary">
            <Zap className="size-4" />
            Workflow automations ({rows.length})
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Event-based rules built into this workflow — they run only on jobs
            this workflow has been applied to. For rules that should run on
            every job, use{" "}
            <Link
              to="/dashboard/automations"
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              general automations
            </Link>
            .
          </p>
        </div>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={openNew}>
            <Plus className="mr-1.5 size-4" /> Add automation
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-6 py-8 text-center text-sm text-muted-foreground">
          No automations yet.
          {canEdit &&
            " Add one — e.g. “when the day one review task is marked Done, notify the partner”."}
        </div>
      ) : (
        <div className="divide-y divide-border rounded-lg border border-border bg-background">
          {rows.map((rule) => (
            <div
              key={rule._id}
              className="flex items-start justify-between gap-3 px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-primary">{rule.name}</p>
                <AutomationRuleSummary
                  orgId={orgId}
                  trigger={rule.trigger}
                  actions={rule.actions}
                  className="mt-1"
                />
              </div>
              <div
                className="flex shrink-0 items-center gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <Switch
                  checked={rule.enabled}
                  disabled={!canEdit}
                  onCheckedChange={(v) => void handleToggle(rule._id, v)}
                />
                {canEdit && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Edit"
                      onClick={() => openEdit(rule)}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Delete"
                      onClick={() =>
                        setPendingDelete({ id: rule._id, name: rule.name })
                      }
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editorOpen && (
        <AutomationRuleDialog
          key={editing?._id ?? "new"}
          orgId={orgId}
          open={editorOpen}
          onOpenChange={setEditorOpen}
          initial={editing}
          templateId={templateId}
          templateName={templateName}
          workflowTaskTitles={taskTitles}
        />
      )}

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this automation?</AlertDialogTitle>
            <AlertDialogDescription>
              “{pendingDelete?.name}” will stop running on jobs using this
              workflow. Its run history is kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
